import React, { useMemo, useRef } from 'react';
import {
  ActionPopup,
  Icon,
  List,
  Text,
  AssetPreview
} from '@shootsta/common-react';
import moment from 'moment';
import type { RouterHistory } from '@core/shared';
import { PRODUCT_TYPE } from '@shootsta/common-domain-data';

import { STATUS_COLOR_V2 } from '../../constants/videoStatusColorMap';
import ThumbnailAssetSelector from '../../../ThumbnailAssetSelector';

import getMasterOutput from '../../utils/getMasterOutput';
import getBasePathByProductType from '../../utils/getBasePathByProductType';

import VideoId from '../../../VideoId';
import elevatePath from '../../constants/elevatePath';
import elevateWorkFlowV2Path from '../../constants/elevateV2Path';

import getStatusForVideo from '../../utils/getStatusForVideo';

import './styles/_project-list-item-v2.scss';

type ProjectListItemProps = {
  isShowArchived: boolean;
  onToggleArchived: any;
  video: Video;
  onDelete: any;
  onDuplicate: any;
  onShare: any;
  onRename: any;
  onProjectCostSummary: any;
  markProjectAsRead: any;
  onPublicUpload: any;
  history: RouterHistory;
  product?: string;
  globalStore: any;
  flags?: any;
};

export const VIDEO_STATUSES = {
  DRAFT: { name: 'DRAFT', label: 'Draft' },
  AWAITING_ACTION: { name: 'AWAITING_ACTION', label: 'Pending Action' },
  REQUIRES_ACTION: { name: 'REQUIRES_ACTION', label: 'Requires Action' },
  ON_HOLD: { name: 'ON_HOLD', label: 'On Hold' },
  COMPLETE: { name: 'COMPLETE', label: 'Complete' },
  IN_PROGRESS: { name: 'IN_PROGRESS', label: 'In Progress' },
  SIGN_OFF_FINAL: { name: 'SIGN_OFF_FINAL', label: 'Pending Sign-off' }
} as const;

function capitalizeFirstLetter(str = '') {
  if (typeof str !== 'string' || str.length === 0) {
    return str;
  }
  return str.charAt(0).toLocaleUpperCase() + str.slice(1).toLocaleLowerCase();
}

function withNoEventPropagation(callback: any) {
  return (e: any) => {
    e.stopPropagation();
    e.preventDefault();

    callback(e);
  };
}

const onItemClick = (
  video: Video,
  history: any,
  actionPopupRef: {
    current: undefined;
  },
  product: undefined | string,
  globalStore: any,
  flags: any,
  status: any,
  markProjectAsRead: any
) =>
  withNoEventPropagation((e) => {
    if (
      actionPopupRef &&
      actionPopupRef.current &&
      actionPopupRef.current.contains(e.target)
    ) {
      return;
    }

    const { getObjectForKey, updateObjectForKey } = globalStore;

    const basePath = getBasePathByProductType(product);

    const { serviceType } = video;

    const { elevateWorkFlowV2 = false } = flags;
    let newPath = `${basePath}/project/${video.videoId}/`;

    if (
      elevatePath === basePath &&
      serviceType &&
      elevateWorkFlowV2 &&
      status === VIDEO_STATUSES.DRAFT
    ) {
      newPath = `${basePath}${`${elevateWorkFlowV2Path}/${video.videoId}/`}`;
    }

    if (!getObjectForKey(newPath).video) {
      updateObjectForKey(newPath, {
        video,
        project: {},
        loading: false
      });
    }

    history.rootPush(newPath);
    markProjectAsRead(video);
  });

const useArchivedOptions = ({
  isShowArchived,
  video,
  onToggleArchived,
  onDelete
}) => {
  const allowedArchiveStatuses = ['DRAFT', 'APPROVED'];

  const hasApprovedMaster = allowedArchiveStatuses.includes(
    video.outputs?.find(({ isMaster }) => isMaster)?.status?.name
  );

  return useMemo(() => {
    if (!hasApprovedMaster) return [];

    const archiveAction = {
      action: withNoEventPropagation(onToggleArchived),
      label: isShowArchived ? 'Unarchive' : 'Archive',
      ...(isShowArchived ? {} : { isRed: true })
    };

    const deleteAction = isShowArchived
      ? [
          {
            action: withNoEventPropagation(onDelete),
            label: 'Delete',
            isRed: true
          }
        ]
      : [];

    return [archiveAction, ...deleteAction];
  }, [isShowArchived, video, onToggleArchived, onDelete]);
};

const useActionPopupOptions = ({
  video,
  history,
  isShowArchived,
  onShare,
  onRename,
  onDuplicate,
  onProjectCostSummary,
  onPublicUpload,
  onToggleArchived,
  onDelete,
  product
}) => {
  const { name } = video;

  const archivedOptions = useArchivedOptions({
    isShowArchived,
    video,
    onToggleArchived,
    onDelete
  });

  const status = getStatusForVideo(video, VIDEO_STATUSES);

  const actions = [
    ...(product !== PRODUCT_TYPE.PITCHTEMPLATE
      ? [
          {
            action: withNoEventPropagation(onShare),
            label: 'Share With Others'
          }
        ]
      : []),
    { action: withNoEventPropagation(onRename), label: 'Rename' },
    { action: withNoEventPropagation(onDuplicate), label: 'Duplicate' },
    {
      action: withNoEventPropagation(onProjectCostSummary),
      label: 'Project cost summary'
    },
    { separator: true }
  ];

  if (status.name !== VIDEO_STATUSES.COMPLETE.name) {
    actions.push({
      action: withNoEventPropagation(onPublicUpload),
      label: 'Team Uploader'
    });
  }

  actions.push(...archivedOptions);

  return useMemo(
    () => actions,
    [
      video,
      history,
      name,
      onShare,
      onRename,
      onDuplicate,
      archivedOptions,
      onProjectCostSummary,
      onPublicUpload
    ]
  );
};

export default function ProjectListItemV2({
  history,
  video,
  onDelete,
  onShare,
  isShowArchived,
  onToggleArchived,
  onDuplicate,
  onRename,
  onPublicUpload,
  onProjectCostSummary,
  markProjectAsRead,
  product,
  globalStore,
  flags = {}
}: ProjectListItemProps) {
  const {
    outputs,
    clips,
    readableId,
    name: videoName,
    projectType,
    createdAt
  } = video;

  const actionPopupRef = useRef();
  const masterOutput = getMasterOutput(video);
  const status = getStatusForVideo(video, VIDEO_STATUSES);
  const onClick = onItemClick(
    video,
    history,
    actionPopupRef,
    product,
    globalStore,
    flags,
    status,
    markProjectAsRead
  );
  const statusColor = STATUS_COLOR_V2[status.name];
  const actionPopupOptions = useActionPopupOptions({
    video,
    history,
    videoName,
    isShowArchived,
    onShare,
    onRename,
    onDuplicate,
    onToggleArchived,
    onDelete,
    onProjectCostSummary,
    onPublicUpload,
    product
  });

  if (!masterOutput) {
    return null;
  }

  const { versions, latestVersion } = masterOutput;
  const { assetId } =
    versions.find(({ versionNumber }) => versionNumber === latestVersion) || {};

  return (
    <ThumbnailAssetSelector
      assetId={assetId}
      videoAssetIds={clips.map((clip) => clip.assetId).filter(Boolean)}
    >
      {(thumbnailAsset) => (
        <List.CardItem
          onClick={onClick}
          customCoverImage={
            thumbnailAsset && (
              <AssetPreview
                asset={thumbnailAsset}
                hideFileTypeIcon
                canPlayVideo={false}
                darkMode
              />
            )
          }
          coverImageProps={{
            defaultImageIcon: 'video',
            defaultIconColour: 'russian',
            defaultIconSize: 50
          }}
        >
          <div
            className="project-list-item-v2__preview__status"
            style={{ background: statusColor }}
          >
            <Text
              darkTheme={
                status.name === 'ON_HOLD' || status.name === 'SIGN_OFF_FINAL'
              }
              noMargin
              centered
            >
              {status.label}
            </Text>
          </div>
          <VideoId videoId={readableId ?? 'N/A'} />
          <div className="project-list-item-v2__body">
            <div className="project-list-item-v2__body__description">
              <div
                className="project-list-item-v2__body__description__name"
                title={videoName}
              >
                <Text heading4 noMargin ellipsisOverflow>
                  {videoName}
                </Text>
                {status.name === 'ON_HOLD' ? (
                  <Icon name="alert" color="thunderBird" />
                ) : null}
              </div>
              <div className="project-list-item-v2__body__description__project-type">
                <Text noMargin>{capitalizeFirstLetter(projectType)}</Text>
              </div>
            </div>
            <ActionPopup
              ref={actionPopupRef.current}
              options={actionPopupOptions}
            />
          </div>
          <div className="project-list-item-v2__bottom">
            <div className="project-list-item-v2__bottom__create-at">
              <Text>
                {moment(createdAt, ['x', 'YYYY-MM-DDTHH:mm:ss.sssZ']).format(
                  'DD MMM, YYYY'
                )}
              </Text>
            </div>
            <div className="project-list-item-v2__bottom__count">
              <Text>{`${outputs.length} Video${outputs.length !== 1 ? 's' : ''}`}</Text>
            </div>
          </div>
        </List.CardItem>
      )}
    </ThumbnailAssetSelector>
  );
}
